export const VIEWER_SURFACE_MCP_APP = "mcp_app";
export const VIEWER_SURFACE_PIXMOTO = "pixmoto";
export const VIEWER_SURFACE_PUBLISHER_SITE = "publisher_site";
// Pixie alone on a publisher's own site (the /pixie page). Never classified
// from the page: only that page's host says it.
export const VIEWER_SURFACE_PUBLISHER_WIDGET = "publisher_widget";

const PIXMOTO_PLAYER_HOST_LOCAL = ["localhost", "127.0.0.1"];
const PIXMOTO_PLAYER_HOST_DEV = "pixmoto-player.automaze.dev";
const PIXMOTO_PLAYER_HOST_STAGE = "pixmoto-player-stage.automaze.dev";
const PIXMOTO_PLAYER_HOST_PROD = "play.pixmoto.com";

export type ViewerSurface =
  | typeof VIEWER_SURFACE_MCP_APP
  | typeof VIEWER_SURFACE_PIXMOTO
  | typeof VIEWER_SURFACE_PUBLISHER_SITE
  | typeof VIEWER_SURFACE_PUBLISHER_WIDGET;

/** What classify reads: the page query, host name and whether it is the top frame. */
export interface ViewerEnv {
  search?: string;
  hostname?: string;
  topLevel?: boolean;
}

let cached: ViewerSurface | null = null;
let windowOverride: Window | null = null;

function hostWindow(): Window | null {
  if (windowOverride) {
    return windowOverride;
  }
  if (typeof window !== "undefined" && window) {
    return window;
  }
  return null;
}

export function isPixmotoPlayHost(hostname: unknown): boolean {
  let h = String(hostname || "").toLowerCase();
  const colon = h.indexOf(":");
  if (colon >= 0 && h.indexOf("[") < 0) {
    h = h.slice(0, colon);
  }
  if (!h) {
    return false;
  }
  if (PIXMOTO_PLAYER_HOST_LOCAL.indexOf(h) !== -1) {
    return true;
  }
  if (
    h === PIXMOTO_PLAYER_HOST_DEV ||
    h.endsWith(`.${PIXMOTO_PLAYER_HOST_DEV}`)
  ) {
    return true;
  }
  if (
    h === PIXMOTO_PLAYER_HOST_STAGE ||
    h.endsWith(`.${PIXMOTO_PLAYER_HOST_STAGE}`)
  ) {
    return true;
  }
  if (
    h === PIXMOTO_PLAYER_HOST_PROD ||
    h.endsWith(`.${PIXMOTO_PLAYER_HOST_PROD}`)
  ) {
    return true;
  }
  return false;
}

function isTopLevel(win: Window): boolean {
  try {
    return win.self === win.top;
  } catch {
    // A cross-origin parent can throw on window.top. That is a publisher
    // embed, so it is not the standalone player.
    return false;
  }
}

// Same query the MCP shell sets and mcpSurface.isMcpAppSurface reads. Taken
// from the env so a passed-in environment (and the test window override) can
// reach mcp_app without touching the live page URL.
function isMcpAppSearch(search: string | undefined): boolean {
  try {
    return new URLSearchParams(search || "").get("pix_surface") === "mcp-app";
  } catch {
    return false;
  }
}

function classify(env: ViewerEnv): ViewerSurface {
  if (isMcpAppSearch(env && env.search)) {
    return VIEWER_SURFACE_MCP_APP;
  }
  if (env.topLevel && isPixmotoPlayHost(env.hostname)) {
    return VIEWER_SURFACE_PIXMOTO;
  }
  return VIEWER_SURFACE_PUBLISHER_SITE;
}

function readWindowEnv(): ViewerEnv {
  const win = hostWindow();
  if (!win) {
    return { search: "", hostname: "", topLevel: false };
  }
  let search = "";
  let hostname = "";
  try {
    search = (win.location && win.location.search) || "";
    hostname = (win.location && win.location.hostname) || "";
  } catch {
    search = "";
    hostname = "";
  }
  return {
    search: search,
    hostname: hostname,
    topLevel: isTopLevel(win),
  };
}

/**
 * The surface for this load. Pass `{search, hostname, topLevel}` to classify
 * a specific environment (tests). The no-arg form reads the page once and
 * returns that same value for the rest of the load.
 */
export function resolveViewerSurface(env?: ViewerEnv | null): ViewerSurface {
  if (env) {
    return classify(env);
  }
  if (cached) {
    return cached;
  }
  cached = classify(readWindowEnv());
  return cached;
}

export function setViewerSurfaceWindowForTests(win: Window | null) {
  windowOverride = win || null;
  cached = null;
}

export function resetViewerSurfaceForTests() {
  cached = null;
  windowOverride = null;
}
